import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { decodeJwt } from 'jose';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import AuthService from 'lib/auth/AuthService';
import {
  registerAccount,
  deleteToken,
  selectRegisterAccountLoading,
  selectRegisterAccountToken
} from 'store/onboarding';
import { isTokenAlive, decodeAndReadToken } from 'lib/auth/tokenOperations';
import { ONBOARDING } from '../UserOnboarding';

import OnboardingViewBase from '../OnboardingViewBase';
import RegisterUserPanel from './RegisterUserPanel';
import { Grid, GridCell, Heading, Text } from 'shared';
import { LanguageSwitcherWidget } from 'v5/features/core/LanguageSwitcherWidget.js';
import { myServices } from 'v5/services/services.connected.app.js';
import { updateAccount } from 'store/accounts/index.js';

const formatString = str => {
  if (!str || typeof str !== 'string' || str.trim().length === 0) {
    return '';
  }
  return str
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const CreateAccount = () => {
  const { t } = useTranslation('v1-features');
  const [safeToRedirect, setSafeToRedirect] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  //redux data
  const isLoading = useSelector(selectRegisterAccountLoading);
  const upgradeToken = useSelector(selectRegisterAccountToken);

  useEffect(() => {
    if (upgradeToken.length > 0) {
      async function buildToken() {
        const token = decodeJwt(upgradeToken);
        token.tokenString = upgradeToken;
        token.workspaces = await AuthService.fetchAccountWorkspaces(
          token.tokenString
        );
        AuthService.token(token);
      }
      buildToken().then(async _ => {
        if (languageChangedByUser) {
          await dispatch(
            updateAccount({
              id: token.account.id,
              data: {
                preferredLanguage: languageChangedByUser
              }
            })
          );
        }

        dispatch(deleteToken());
        setIsExiting(true);
      });
    }
  }, [upgradeToken]);

  const dispatch = useDispatch();
  const cookies = new Cookies();
  const queryToken = cookies.get(ONBOARDING);
  const isAlive = isTokenAlive(queryToken);

  const [languageChangedByUser, setLanguageChangedByUser] = useState();

  if (!isAlive) return <Redirect to="/login" />;

  const { token, bearerToken } = decodeAndReadToken(queryToken);

  const teamSchema = token.teamSchema;
  const invitedEmail = token.invited.invitedEmail;
  const invitedBy = token.invited.invitedByFirstName;

  if (safeToRedirect) {
    return (
      <Redirect
        to={{
          pathname: `/app/${teamSchema}/onboarding/create-profile`,
          state: { teamSchema }
        }}
      />
    );
  }

  const onSubmit = async formData => {
    const data = { ...formData, authMethod: 'local' };
    await dispatch(registerAccount({ data, teamSchema, token: bearerToken }));
  };

  const handleAnimationComplete = () => {
    if (isExiting) {
      setSafeToRedirect(true);
    }
  };

  return (
    <OnboardingViewBase>
      <div className="tailwind-root contents">
        <LanguageSwitcherWidget
          className="fixed top-3 right-3"
          services={myServices}
          // this way we know if user selected a language so we will store it in its settings for him
          // otherwise the language will be system default
          onLanguageChange={setLanguageChangedByUser}
        />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: isExiting ? 0 : 1, y: isExiting ? -10 : 0 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        onAnimationComplete={handleAnimationComplete}
      >
        <Heading type="h2" stack="xs">
          {t('CreateAccount.mainHeader')}
        </Heading>
        <Text size="m" color="black" stack="l">
          {t('CreateAccount.invitation', { invitedBy })}
        </Text>
        <RegisterUserPanel
          onSubmit={onSubmit}
          invitedEmail={invitedEmail}
          isLoading={isLoading}
        />
      </motion.div>
    </OnboardingViewBase>
  );
};

export default CreateAccount;
